/**
 * IframeBlobWrapper
 * Reusable iframe component for ACH content with postMessage communication
 * Supports content CRUD operations via iframe bridge
 */

import React, {
    useRef,
    useEffect,
    useState,
    useCallback,
    forwardRef,
    useImperativeHandle,
} from 'react';
import type { ContentItem, ContentConfig, ContentResult } from '@ai-content-hub/content-core';
import {
    ACH_EMBED_IFRAME_ALLOW,
    applyEmbedLanguageToConfig,
    normalizeContentLocale,
} from '@ai-content-hub/content-core';
import {
    createEditorBlobUrl,
    createPlayerBlobUrl,
    revokeBlobUrl,
    type IframeMessage,
} from './blobIframe';
import {
    getCachedCustomTheme,
    getThemeMode,
    THEME_MODE_PARAM,
} from '@ai-content-hub/shared-styles';

// Content CRUD message types (compatible with content-api iframe-bridge)
export type ContentMessageType =
    | 'CONTENT_GET'
    | 'CONTENT_DATA'
    | 'CONTENT_SAVE'
    | 'CONTENT_UPDATE'
    | 'CONTENT_SAVED'
    | 'CONTENT_DELETE'
    | 'CONTENT_DELETED'
    | 'CONTENT_ERROR';

export interface IframeBlobWrapperRef {
    getContent: () => ContentItem | null;
    sendMessage: (type: string, payload?: unknown) => void;
    // Content CRUD methods
    getContentById: (contentId: string) => Promise<ContentItem>;
    saveContent: (data: unknown) => Promise<unknown>;
    updateContent: (contentId: string, data: unknown) => Promise<unknown>;
    deleteContent: (contentId: string) => Promise<void>;
}

/**
 * - `content`: wrapper height follows postMessage EDITOR_HEIGHT_CHANGE / PLAYER_HEIGHT_CHANGE (pixel height).
 * - `container`: wrapper fills the parent (height 100%, flex); postMessage height updates are ignored.
 */
export type IframeHeightMode = 'content' | 'container';

export interface IframeBlobWrapperProps {
    mode: 'editor' | 'player';
    content: ContentItem;
    config?: ContentConfig;
    embedUrl?: string;
    readOnly?: boolean;
    /** Passed to embed via query (contentType=). Embed uses for create/default type. */
    contentTypeSlug?: string;
    /** Passed to embed via query (showPreview=). Embed toggles preview inside iframe. */
    showPreview?: boolean;
    /** Passed to embed via query (hiddenHeader=). Embed hides header bar when true. */
    hiddenHeader?: boolean;
    /**
     * How the outer wrapper gets its height. Default `content` uses iframe content measurement;
     * use `container` when the parent supplies a bounded height (e.g. split panel).
     */
    iframeHeightMode?: IframeHeightMode;
    /** Used for loading placeholder and as floor when applying postMessage height (`content` mode). */
    minHeight?: number;
    onChange?: (content: ContentItem) => void;
    onAnswer?: (answer: unknown) => void;
    onSubmit?: (result: ContentResult) => void;
    onComplete?: (result: ContentResult) => void;
    onReady?: () => void;
    onError?: (error: string) => void;
    // Content CRUD callbacks
    onContentSaved?: (content: unknown, isNew: boolean) => void;
    onContentDeleted?: (contentId: string) => void;
    // API configuration for embed pages
    apiConfig?: {
        baseUrl?: string;
        getAuthToken?: () => string | null;
    };
    /** Called when the iframe reports its resolved layout type (e.g. 'horizontal'). */
    onLayoutType?: (layoutType: string) => void;
    /** Appended to production embed URL as `userId=` (e.g. speaking AI grading). */
    userId?: string;
    /** UI locale from host app; passed as `lang=` on embed URL and `i18n.defaultLanguage` in INIT. */
    language?: string;
    /** Tenant ID for staff JWT context; passed as `tenant_id=` on embed URL and in INIT payload. */
    tenantId?: string;
    /**
     * Stable postMessage / embed-URL channel id. When set, iframe is not reloaded when
     * `content.id` changes — parent sends PLAYER_INIT with the new content instead.
     * Use for preview carousels that hop between many questions in one session.
     */
    channelId?: string;
}

export const IframeBlobWrapper = forwardRef<IframeBlobWrapperRef, IframeBlobWrapperProps>(
    (
        {
            mode,
            content,
            config,
            embedUrl,
            readOnly = false,
            showPreview,
            hiddenHeader,
            contentTypeSlug,
            iframeHeightMode = 'content',
            minHeight = 300,
            onChange,
            onAnswer,
            onSubmit,
            onComplete,
            onReady,
            onError,
            onContentSaved,
            onContentDeleted,
            apiConfig,
            onLayoutType,
            userId,
            language,
            tenantId,
            channelId,
        },
        ref
    ) => {
        const iframeRef = useRef<HTMLIFrameElement>(null);
        const contentRef = useRef<ContentItem>(content);
        const resolvedLanguage = language ? normalizeContentLocale(language) : undefined;
        const configWithLanguage = React.useMemo(
            () => applyEmbedLanguageToConfig(config, resolvedLanguage),
            [config, resolvedLanguage],
        );
        const configRef = useRef<ContentConfig | undefined>(configWithLanguage);
        const lastHeightRef = useRef<number>(minHeight);
        const heightCooldownUntilRef = useRef<number>(0);
        const [isReady, setIsReady] = useState(false);
        const [iframeHeight, setIframeHeight] = useState(minHeight);
        const [blobUrl, setBlobUrl] = useState<string | null>(null);

        const useContentHeight = iframeHeightMode === 'content';

        const HEIGHT_CHANGE_THRESHOLD = 20;
        const HEIGHT_COOLDOWN_MS = 600;

        // Pending requests for content CRUD operations
        const pendingRequests = useRef<
            Map<string, { resolve: (v: unknown) => void; reject: (e: unknown) => void }>
        >(new Map());

        // Stable fallback id so we don't get a new blob URL every render (which would reload iframe and cause refresh loop)
        const fallbackIdRef = useRef<string | null>(null);
        if (fallbackIdRef.current === null && !content.id) {
            fallbackIdRef.current = `ach-${mode}-${Date.now()}`;
        }
        const contentId =
            channelId ?? content.id ?? fallbackIdRef.current ?? `ach-${mode}-${Date.now()}`;

        // Update content ref when content changes
        useEffect(() => {
            contentRef.current = content;
        }, [content]);

        // Update config ref when config changes
        useEffect(() => {
            configRef.current = configWithLanguage;
        }, [configWithLanguage]);

        // Send message to iframe
        const sendMessage = useCallback(
            (type: string, payload?: unknown, targetContentId?: string) => {
                if (!iframeRef.current?.contentWindow) return;

                const message: IframeMessage = {
                    type: type as IframeMessage['type'],
                    contentId: targetContentId || contentId,
                    payload,
                    timestamp: Date.now(),
                };

                iframeRef.current.contentWindow.postMessage(message, '*');
            },
            [contentId]
        );

        // Generate unique request ID
        const generateRequestId = useCallback(() => {
            return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        }, []);

        // Send request and wait for response
        const sendRequest = useCallback(
            <T,>(type: string, payload?: unknown, targetContentId?: string): Promise<T> => {
                return new Promise((resolve, reject) => {
                    const requestId = generateRequestId();
                    pendingRequests.current.set(requestId, {
                        resolve: resolve as (v: unknown) => void,
                        reject,
                    });

                    // Timeout after 30 seconds
                    const timeoutId = setTimeout(() => {
                        if (pendingRequests.current.has(requestId)) {
                            pendingRequests.current.delete(requestId);
                            reject(new Error('Request timeout'));
                        }
                    }, 30000);

                    // Store timeout ID for cleanup
                    const entry = pendingRequests.current.get(requestId);
                    if (entry) {
                        (
                            entry as unknown as { timeoutId: ReturnType<typeof setTimeout> }
                        ).timeoutId = timeoutId;
                    }

                    const payloadObj = (
                        payload && typeof payload === 'object' ? payload : {}
                    ) as Record<string, unknown>;
                    sendMessage(type, { ...payloadObj, requestId }, targetContentId);
                });
            },
            [sendMessage, generateRequestId]
        );

        // Expose methods via ref
        useImperativeHandle(
            ref,
            () => ({
                getContent: () => contentRef.current,
                sendMessage,
                // Content CRUD methods
                getContentById: async (targetContentId: string) => {
                    const result = await sendRequest<{ content: ContentItem }>(
                        'CONTENT_GET',
                        undefined,
                        targetContentId
                    );
                    return result.content;
                },
                saveContent: async (data: unknown) => {
                    const result = await sendRequest<{ content: unknown }>('CONTENT_SAVE', {
                        data,
                    });
                    return result.content;
                },
                updateContent: async (targetContentId: string, data: unknown) => {
                    const result = await sendRequest<{ content: unknown }>('CONTENT_UPDATE', {
                        contentId: targetContentId,
                        data,
                    });
                    return result.content;
                },
                deleteContent: async (targetContentId: string) => {
                    await sendRequest<void>('CONTENT_DELETE', undefined, targetContentId);
                },
            }),
            [sendMessage, sendRequest]
        );

        // Clean up pending requests on unmount to prevent memory leaks
        useEffect(() => {
            return () => {
                pendingRequests.current.forEach(({ reject }, key) => {
                    const entry = pendingRequests.current.get(key);
                    if (entry) {
                        const timeoutId = (
                            entry as unknown as { timeoutId?: ReturnType<typeof setTimeout> }
                        ).timeoutId;
                        if (timeoutId) clearTimeout(timeoutId);
                    }
                    reject(new Error('Component unmounted'));
                });
                pendingRequests.current.clear();
            };
        }, []);

        // Create blob URL or use embed URL
        useEffect(() => {
            if (embedUrl) {
                // Use production URL; pass contentType and showPreview so embed can read from query
                const params = new URLSearchParams();
                params.set('contentId', contentId);
                if (contentTypeSlug) params.set('contentType', contentTypeSlug);
                if (showPreview !== undefined) params.set('showPreview', String(showPreview));
                if (hiddenHeader) params.set('hiddenHeader', 'true');
                if (userId) params.set('userId', userId);
                if (resolvedLanguage) params.set('lang', resolvedLanguage);
                if (tenantId) params.set('tenant_id', tenantId);
                // EAA-755: the embed is a separate document (often a separate
                // origin), so it sees neither the host's `app-theme-mode` nor its
                // `data-theme` and booted light inside a dark shell. `theme`
                // below carries the tenant's PALETTE; this carries which half of
                // it to use. Read at URL-build time on purpose — making it
                // reactive would rewrite `src` on every toggle and reload the
                // embed; live toggles come through the `theme-mode` postMessage.
                params.set(THEME_MODE_PARAM, getThemeMode());
                // Pass custom theme config so the editor iframe applies it on load
                // EAA-1664: the cache is tenant-scoped (`theme:{tenantId}`) — without
                // tenantId this read the empty legacy slot and the iframe never got
                // the tenant's theme override (content player buttons stayed on the
                // base --primary color regardless of the active tenant's brand).
                const themeConfig = getCachedCustomTheme(tenantId);
                if (themeConfig) {
                    try {
                        params.set('theme', btoa(JSON.stringify(themeConfig)));
                    } catch {
                        /* ignore */
                    }
                }
                const url = `${embedUrl}/embed/${mode}?${params.toString()}`;
                setBlobUrl(url);
                setIsReady(false);
            } else {
                // Create blob URL for development
                const url =
                    mode === 'editor'
                        ? createEditorBlobUrl(contentId, tenantId)
                        : createPlayerBlobUrl(contentId, tenantId);
                setBlobUrl(url);

                // Cleanup blob URL on unmount
                return () => {
                    revokeBlobUrl(url);
                };
            }
        }, [
            embedUrl,
            mode,
            contentId,
            channelId,
            contentTypeSlug,
            showPreview,
            hiddenHeader,
            userId,
            resolvedLanguage,
            tenantId,
        ]);

        // Handle editor-specific messages from iframe
        const handleEditorMessages = useCallback(
            (message: IframeMessage) => {
                switch (message.type) {
                    case 'EDITOR_READY':
                        setIsReady(true);
                        // Send initial content with API config if provided
                        sendMessage('EDITOR_INIT', {
                            content,
                            config: configWithLanguage,
                            readOnly,
                            mode,
                            ...(tenantId && { tenantId }),
                            ...(apiConfig?.baseUrl && {
                                baseUrl: apiConfig.baseUrl,
                                ...(apiConfig.getAuthToken && {
                                    apiToken: apiConfig.getAuthToken() || undefined,
                                }),
                            }),
                        });
                        onReady?.();
                        break;

                    case 'EDITOR_GET_CONTENT':
                        // Iframe is requesting current content - send it back
                        sendMessage('EDITOR_CONTENT_DATA', {
                            content: contentRef.current,
                        });
                        break;

                    case 'EDITOR_HEIGHT_CHANGE':
                        if (!useContentHeight) break;
                        if (message.payload && typeof message.payload === 'object') {
                            const height = (message.payload as { height: number }).height;
                            if (typeof height === 'number' && height > 0) {
                                const newHeight = Math.max(height, minHeight);
                                const isSignificantGrowth =
                                    newHeight - lastHeightRef.current > HEIGHT_CHANGE_THRESHOLD;
                                const inCooldown = Date.now() < heightCooldownUntilRef.current;
                                if (inCooldown && !isSignificantGrowth) break;
                                if (
                                    Math.abs(newHeight - lastHeightRef.current) >
                                        HEIGHT_CHANGE_THRESHOLD ||
                                    isSignificantGrowth
                                ) {
                                    lastHeightRef.current = newHeight;
                                    heightCooldownUntilRef.current =
                                        Date.now() + HEIGHT_COOLDOWN_MS;
                                    setIframeHeight(newHeight);
                                }
                            }
                        }
                        break;

                    case 'EDITOR_CONTENT_DATA':
                        if (message.payload && typeof message.payload === 'object') {
                            const payload = message.payload as {
                                content: ContentItem;
                                requestId?: string;
                            };
                            if (
                                payload.requestId &&
                                pendingRequests.current.has(payload.requestId)
                            ) {
                                const { resolve } = pendingRequests.current.get(payload.requestId)!;
                                pendingRequests.current.delete(payload.requestId);
                                resolve(payload);
                            } else if (payload.content) {
                                contentRef.current = payload.content;
                            }
                        }
                        break;

                    case 'EDITOR_ERROR':
                        if (message.payload && typeof message.payload === 'object') {
                            const errorMsg = (message.payload as { message: string }).message;
                            onError?.(errorMsg || 'Unknown error');
                        }
                        break;

                    case 'EDITOR_CONTENT_SAVED':
                        if (message.payload && typeof message.payload === 'object') {
                            const payload = message.payload as {
                                content: unknown;
                                isNew: boolean;
                                requestId?: string;
                            };
                            if (
                                payload.requestId &&
                                pendingRequests.current.has(payload.requestId)
                            ) {
                                const { resolve } = pendingRequests.current.get(payload.requestId)!;
                                pendingRequests.current.delete(payload.requestId);
                                resolve(payload);
                            }
                            onContentSaved?.(payload.content, payload.isNew);
                        }
                        break;

                    case 'EDITOR_CONTENT_DELETED':
                        if (message.payload && typeof message.payload === 'object') {
                            const payload = message.payload as {
                                contentId: string;
                                requestId?: string;
                            };
                            if (
                                payload.requestId &&
                                pendingRequests.current.has(payload.requestId)
                            ) {
                                const { resolve } = pendingRequests.current.get(payload.requestId)!;
                                pendingRequests.current.delete(payload.requestId);
                                resolve(undefined);
                            }
                            onContentDeleted?.(payload.contentId);
                        }
                        break;

                    case 'EDITOR_CONTENT_ERROR':
                        if (message.payload && typeof message.payload === 'object') {
                            const payload = message.payload as {
                                message: string;
                                requestId?: string;
                            };
                            if (
                                payload.requestId &&
                                pendingRequests.current.has(payload.requestId)
                            ) {
                                const { reject } = pendingRequests.current.get(payload.requestId)!;
                                pendingRequests.current.delete(payload.requestId);
                                reject(new Error(payload.message));
                            } else {
                                onError?.(payload.message || 'Content operation error');
                            }
                        }
                        break;

                    case 'EDITOR_CONTENT_CHANGE':
                        if (message.payload && typeof message.payload === 'object') {
                            const updatedContent = (message.payload as { content: ContentItem })
                                .content;
                            if (updatedContent) {
                                contentRef.current = updatedContent;
                                onChange?.(updatedContent);
                            }
                        }
                        break;
                }
            },
            [
                content,
                config,
                readOnly,
                mode,
                minHeight,
                useContentHeight,
                sendMessage,
                onReady,
                onError,
                onContentSaved,
                onContentDeleted,
                onChange,
                apiConfig,
            ]
        );

        // Handle player-specific messages from iframe
        const handlePlayerMessages = useCallback(
            (message: IframeMessage) => {
                switch (message.type) {
                    case 'PLAYER_READY':
                        setIsReady(true);
                        // Send initial content with API config if provided
                        sendMessage('PLAYER_INIT', {
                            content,
                            config,
                            readOnly,
                            mode,
                            ...(tenantId && { tenantId }),
                            ...(apiConfig?.baseUrl && {
                                baseUrl: apiConfig.baseUrl,
                                ...(apiConfig.getAuthToken && {
                                    apiToken: apiConfig.getAuthToken() || undefined,
                                }),
                            }),
                        });
                        onReady?.();
                        break;

                    case 'PLAYER_HEIGHT_CHANGE':
                        if (!useContentHeight) {
                            // Even in container mode, extract layoutType if present
                            if (message.payload && typeof message.payload === 'object') {
                                const lt = (message.payload as { layoutType?: string }).layoutType;
                                if (lt) onLayoutType?.(lt);
                            }
                            break;
                        }
                        if (message.payload && typeof message.payload === 'object') {
                            const p = message.payload as { height: number; layoutType?: string };
                            const height = p.height;
                            if (p.layoutType) onLayoutType?.(p.layoutType);
                            if (typeof height === 'number' && height > 0) {
                                const newHeight = Math.max(height, minHeight);
                                const isSignificantGrowth =
                                    newHeight - lastHeightRef.current > HEIGHT_CHANGE_THRESHOLD;
                                const inCooldown = Date.now() < heightCooldownUntilRef.current;
                                if (inCooldown && !isSignificantGrowth) break;
                                if (
                                    Math.abs(newHeight - lastHeightRef.current) >
                                        HEIGHT_CHANGE_THRESHOLD ||
                                    isSignificantGrowth
                                ) {
                                    lastHeightRef.current = newHeight;
                                    heightCooldownUntilRef.current =
                                        Date.now() + HEIGHT_COOLDOWN_MS;
                                    setIframeHeight(newHeight);
                                }
                            }
                        }
                        break;

                    case 'PLAYER_ANSWER':
                        if (message.payload) {
                            onAnswer?.(message.payload);
                        }
                        break;

                    case 'PLAYER_SUBMIT':
                        if (message.payload) {
                            onSubmit?.(message.payload as ContentResult);
                        }
                        break;

                    case 'PLAYER_COMPLETE':
                        if (message.payload) {
                            onComplete?.(message.payload as ContentResult);
                        }
                        break;
                }
            },
            [
                content,
                config,
                readOnly,
                mode,
                minHeight,
                useContentHeight,
                sendMessage,
                onReady,
                onAnswer,
                onSubmit,
                onComplete,
                apiConfig,
                onLayoutType,
            ]
        );

        // Main message handler - routes to appropriate handler based on mode
        useEffect(() => {
            const handleMessage = (event: MessageEvent) => {
                const message = event.data as IframeMessage;
                if (!message || message.contentId !== contentId) return;

                // Handle mode-specific messages
                if (mode === 'editor') {
                    handleEditorMessages(message);
                } else if (mode === 'player') {
                    handlePlayerMessages(message);
                }
            };

            window.addEventListener('message', handleMessage);
            return () => window.removeEventListener('message', handleMessage);
        }, [contentId, mode, handleEditorMessages, handlePlayerMessages]);

        // Send updated content when it changes
        useEffect(() => {
            if (isReady) {
                const initMsg = mode === 'editor' ? 'EDITOR_INIT' : 'PLAYER_INIT';
                sendMessage(initMsg, {
                    content,
                    config: configWithLanguage,
                    readOnly,
                    mode,
                    ...(tenantId && { tenantId }),
                    ...(apiConfig?.baseUrl && {
                        baseUrl: apiConfig.baseUrl,
                        ...(apiConfig.getAuthToken && {
                            apiToken: apiConfig.getAuthToken() || undefined,
                        }),
                    }),
                });

                // Send custom theme to URL-based iframes via postMessage
                // EAA-1664: tenant-scoped cache — see comment above.
                if (embedUrl) {
                    const themeConfig = getCachedCustomTheme(tenantId);
                    iframeRef.current?.contentWindow?.postMessage(
                        { type: 'custom-theme', config: themeConfig },
                        '*'
                    );
                }
            }
        }, [
            isReady,
            content,
            configWithLanguage,
            readOnly,
            mode,
            sendMessage,
            apiConfig,
            embedUrl,
        ]);

        // Also send CONFIG_UPDATE when config changes (for iframe to handle config-only updates)
        useEffect(() => {
            if (isReady && configWithLanguage !== undefined) {
                const prevConfig = configRef.current;
                configRef.current = configWithLanguage;
                if (prevConfig !== undefined && prevConfig !== configWithLanguage) {
                    const configMsg =
                        mode === 'editor' ? 'EDITOR_CONFIG_UPDATE' : 'PLAYER_CONFIG_UPDATE';
                    sendMessage(configMsg, configWithLanguage);
                }
            }
        }, [isReady, configWithLanguage, mode, sendMessage]);

        if (!blobUrl) {
            return (
                <div
                    style={{
                        padding: 24,
                        textAlign: 'center',
                        minHeight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    Initializing {mode}...
                </div>
            );
        }

        return (
            <div
                className={`ach-iframe-wrapper ach-iframe-${mode}`}
                style={{
                    width: '100%',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    ...(useContentHeight
                        ? { minHeight: '100%', height: iframeHeight }
                        : { flex: '1 1 0%', minHeight: 0, height: '100%' }),
                }}
            >
                {!isReady && (
                    <div
                        style={{
                            padding: 24,
                            textAlign: 'center',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            // EAA-755: themed, not a white sheet over dark content.
                            background:
                                'color-mix(in srgb, hsl(var(--background, 0 0% 100%)) 90%, transparent)',
                            zIndex: 1,
                        }}
                    >
                        Loading {mode}...
                    </div>
                )}
                <iframe
                    key={blobUrl}
                    ref={iframeRef}
                    src={blobUrl}
                    allow={ACH_EMBED_IFRAME_ALLOW}
                    style={{
                        width: '100%',
                        height: '100%',
                        minHeight: 0,
                        flex: 1,
                        border: 'none',
                        display: 'block',
                    }}
                    title={`ACH ${mode}`}
                    {...(embedUrl
                        ? {}
                        : {
                              sandbox: 'allow-scripts allow-same-origin allow-forms allow-modals',
                          })}
                />
            </div>
        );
    }
);

IframeBlobWrapper.displayName = 'IframeBlobWrapper';

export default IframeBlobWrapper;
